/**
 * Every action the Worker writes to the audit log, in plain words for the
 * admin's Activity page (Phase 8). The Worker's audit coverage test checks
 * that each `action:` in its code is listed here, and that every route that
 * changes something writes one of them.
 */
export const AUDIT_GROUPS = ['signin', 'people', 'templates', 'projects', 'documents', 'reviews', 'share', 'other'] as const;
export type AuditGroup = (typeof AUDIT_GROUPS)[number];

export const AUDIT_GROUP_LABEL: Record<AuditGroup, string> = {
  signin: 'Sign-in', people: 'People', templates: 'Templates', projects: 'Projects',
  documents: 'Documents', reviews: 'Review requests', share: 'Share links', other: 'Labels and lists',
};

export const AUDIT_ACTIONS: Record<string, { label: string; group: AuditGroup }> = {
  'auth.login': { label: 'Signed in', group: 'signin' },
  'auth.login_failed': { label: 'Failed sign-in (wrong email or password)', group: 'signin' },
  'auth.logout': { label: 'Signed out', group: 'signin' },
  'auth.password_changed': { label: 'Changed password', group: 'signin' },
  'auth.setup_completed': { label: 'Set a password with a setup code', group: 'signin' },
  'auth.refresh_reuse_detected': { label: 'A stolen or old session was refused (all sessions signed out)', group: 'signin' },

  'user.bootstrap_admin': { label: 'First administrator created', group: 'people' },
  'user.create': { label: 'Added a person', group: 'people' },
  'user.update': { label: 'Changed a person (role or status)', group: 'people' },
  'user.reset': { label: 'Reset a person\'s password (new setup code)', group: 'people' },

  'template.create': { label: 'Created a template', group: 'templates' },
  'template.draft_save': { label: 'Saved a template draft', group: 'templates' },
  'template.publish': { label: 'Published a template', group: 'templates' },
  'template.customise': { label: 'Made a custom copy of a template', group: 'templates' },
  'template.labels_set': { label: 'Changed a template\'s labels', group: 'templates' },
  'template.source_upload': { label: 'Uploaded a template\'s Word file', group: 'templates' },

  'project.create': { label: 'Created a project', group: 'projects' },
  'project.update': { label: 'Renamed a project', group: 'projects' },
  'project.delete': { label: 'Deleted a project', group: 'projects' },
  'project.labels_set': { label: 'Changed a project\'s labels', group: 'projects' },
  'project.member_add': { label: 'Added an executive to a project', group: 'projects' },
  'project.member_remove': { label: 'Removed an executive from a project', group: 'projects' },
  'project.template_add': { label: 'Added a template to a project', group: 'projects' },
  'project.template_remove': { label: 'Removed a template from a project', group: 'projects' },

  'document.create': { label: 'Started a document', group: 'documents' },
  'document.draft_save': { label: 'Saved a document draft', group: 'documents' },
  'document.help_open': { label: 'Opened an executive\'s draft to help him (after his review request)', group: 'documents' },
  'document.help_save': { label: 'Filled in an executive\'s draft for him (after his review request)', group: 'documents' },
  'document.render': { label: 'Opened a document for its PDF', group: 'documents' },
  'document.complete': { label: 'Finished a document (PDF downloaded)', group: 'documents' },
  'document.register': { label: 'Entered a document\'s registration number (removed for the executive)', group: 'documents' },
  'document.reopen': { label: 'Opened a finished document for changes again (before its registration number)', group: 'documents' },
  'document.discard': { label: 'Deleted a document draft', group: 'documents' },
  'document.wording_save': { label: 'Changed a document\'s wording', group: 'documents' },
  'document.wording_reset': { label: 'Put a document\'s wording back to the template\'s', group: 'documents' },
  'review.create': { label: 'Asked the admin for a review', group: 'reviews' },
  'review.reply': { label: 'Answered a review request', group: 'reviews' },
  'review.done': { label: 'Marked a review request done', group: 'reviews' },
  'review.delete': { label: 'Removed a review request', group: 'reviews' },

  'share.create': { label: 'Made a share link', group: 'share' },
  'share.revoke': { label: 'Switched off a share link', group: 'share' },
  'share.replace': { label: 'Gave a share link a new address', group: 'share' },
  'share.download': { label: 'A template was downloaded through a share link', group: 'share' },

  'label.create': { label: 'Created a label', group: 'other' },
  'list.create': { label: 'Uploaded a list (old Lists page)', group: 'other' },
  'list.replace': { label: 'Replaced a list (old Lists page)', group: 'other' },
};

export function auditLabel(action: string): string {
  return AUDIT_ACTIONS[action]?.label ?? action;
}

export function actionsInGroup(group: AuditGroup): string[] {
  return Object.entries(AUDIT_ACTIONS).filter(([, a]) => a.group === group).map(([k]) => k);
}
